import type { App, Directive, Plugin } from 'vue';
import { getI18nService } from '../services/i18n';
import { getRouterService } from '../services/router';
import { getStoreService } from '../services/store';

// 应用上下文配置
export interface AppContextConfig {
  directives?: Record<string, Directive>;
  plugins?: Array<{ plugin: Plugin; options?: unknown }>;
}

// 全局应用上下文
export interface AppContext extends AppContextConfig {
  mainApp?: App;
}

// 全局应用上下文实例
let globalAppContext: AppContext = {};

// 设置主应用实例
export const setMainApp = (app: App): void => {
  globalAppContext.mainApp = app;

  // 从主应用中提取全局配置
  if (app.config?.globalProperties) {
    // 如果需要，可以在这里提取其他全局属性
  }
};

// 获取主应用实例
export const getMainApp = (): App | undefined => {
  return globalAppContext.mainApp;
};

// 注册指令到上下文
export const registerDirective = (name: string, directive: Directive): void => {
  if (!globalAppContext.directives) {
    globalAppContext.directives = {};
  }
  globalAppContext.directives[name] = directive;
};

// 注册插件到上下文
export const registerPlugin = (plugin: Plugin, options?: unknown): void => {
  if (!globalAppContext.plugins) {
    globalAppContext.plugins = [];
  }
  globalAppContext.plugins.push({ plugin, options });
};

// 配置独立应用 - 继承主应用的配置
export const configureChildApp = (childApp: App): void => {
  try {
    // 安装 Store 服务（Pinia）
    const storeService = getStoreService();
    if (storeService) {
      storeService.install(childApp);
    }

    // 安装 i18n 服务
    const i18nService = getI18nService();
    if (i18nService) {
      i18nService.install(childApp);
    }

    // 安装路由服务
    const routerService = getRouterService();
    if (routerService) {
      routerService.install(childApp);
    }

    // 注册指令
    if (globalAppContext.directives) {
      Object.entries(globalAppContext.directives).forEach(([name, directive]) => {
        childApp.directive(name, directive);
      });
    }

    // 注册插件
    if (globalAppContext.plugins) {
      globalAppContext.plugins.forEach(({ plugin, options }) => {
        childApp.use(plugin, options);
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to configure child app:', error);
  }
};

// 获取应用上下文配置
export const getAppContext = (): AppContext => {
  return globalAppContext;
};

// 清除应用上下文
export const clearAppContext = (): void => {
  globalAppContext = {};
};
